import * as PopoverPrimitive from '@radix-ui/react-popover'
import { X } from 'lucide-react'
import {
  ComponentProps,
  FC,
  ReactNode,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'

type ButtonComponentProps = ComponentProps<typeof Button>

const FooterContainer: FC<{ children: ReactNode }> = ({ children }) => (
  <div className='flex items-center justify-end gap-2 px-2 pb-2'>{children}</div>
)

export interface FloatingPopoverProps extends PopoverPrimitive.PopoverProps {
  trigger: ReactNode
  triggerAction?: 'click' | 'hover'
  title?: ReactNode
  showClose?: boolean
  children?: ReactNode
  className?: string
  align?: 'center' | 'start' | 'end'
  showArrow?: boolean
  sideOffset?: number
  footer?: ReactNode
  showCancelButton?: boolean
  cancelButtonText?: string
  cancelButtonProps?: ButtonComponentProps
  showPrimaryButton?: boolean
  primaryButtonText?: string
  primaryButtonProps?: ButtonComponentProps
  variant?: 'default' | 'destructive'
  onCancel?: () => void
  onOk?: () => Promise<void> | void
  confirmLoading?: boolean
}

export const FloatingPopover: FC<FloatingPopoverProps> = ({
  trigger,
  triggerAction = 'click',
  title,
  showClose = true,
  children,
  className,
  align = 'center',
  showArrow = true,
  sideOffset = 8,
  footer,
  showCancelButton = true,
  cancelButtonText = 'Cancel',
  cancelButtonProps,
  showPrimaryButton = true,
  primaryButtonText,
  primaryButtonProps,
  variant = 'default',
  onCancel,
  onOk,
  confirmLoading = false,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  ...props
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen)
  const [internalConfirmLoading, setInternalConfirmLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const defaultPrimaryText = variant === 'destructive' ? 'Delete' : 'Save'
  const finalPrimaryText = primaryButtonText || defaultPrimaryText
  const isConfirmLoading = internalConfirmLoading || confirmLoading

  const setOpen = useCallback(
    (value: boolean) => {
      if (!value && isConfirmLoading) return

      if (!isControlled) {
        setUncontrolledOpen(value)
      }
      onOpenChange?.(value)
    },
    [isControlled, isConfirmLoading, onOpenChange],
  )

  const handleCancelClick = useCallback(() => {
    if (isConfirmLoading) return
    onCancel?.()
    setOpen(false)
  }, [isConfirmLoading, onCancel, setOpen])

  const openDelay = 150
  const closeDelay = 300

  const handleMouseEnter = useCallback(() => {
    if (triggerAction !== 'hover') {
      return
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => setOpen(true), openDelay)
  }, [setOpen, triggerAction])

  const handleMouseLeave = useCallback(() => {
    if (triggerAction !== 'hover') {
      return
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => setOpen(false), closeDelay)
  }, [setOpen, triggerAction])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleOkClick = useCallback(async () => {
    if (!onOk || isConfirmLoading) return

    setInternalConfirmLoading(true)
    try {
      await onOk()
    } finally {
      setInternalConfirmLoading(false)
    }
  }, [onOk, isConfirmLoading])

  const renderCloseButton = () => {
    if (!showClose) return null

    return (
      <PopoverPrimitive.Close asChild aria-label='close' className='rounded-md transition-colors'>
        <Button variant='ghost' size='icon-sm' aria-label='close' disabled={isConfirmLoading}>
          <X className='text-main-foreground-primary' size={16} />
        </Button>
      </PopoverPrimitive.Close>
    )
  }

  const renderFooter = () => {
    if (footer) {
      return <FooterContainer>{footer}</FooterContainer>
    }

    const buttons: ReactNode[] = []

    if (showCancelButton) {
      const {
        disabled: cancelDisabledProp,
        onClick: cancelOnClickProp,
        ...restCancelButtonProps
      } = (cancelButtonProps ?? {}) as ButtonComponentProps

      buttons.push(
        <Button
          key='cancel'
          variant='ghost'
          size='sm'
          {...restCancelButtonProps}
          onClick={(event) => {
            cancelOnClickProp?.(event)
            if (event.defaultPrevented) return
            handleCancelClick()
          }}
          disabled={isConfirmLoading || cancelDisabledProp}
        >
          {cancelButtonText}
        </Button>,
      )
    }

    if (showPrimaryButton) {
      const {
        disabled: primaryDisabledProp,
        onClick: primaryOnClickProp,
        ...restPrimaryButtonProps
      } = (primaryButtonProps ?? {}) as ButtonComponentProps

      buttons.push(
        <Button
          key='primary'
          size='sm'
          variant={variant}
          {...restPrimaryButtonProps}
          onClick={(event) => {
            primaryOnClickProp?.(event)
            if (event.defaultPrevented) return
            handleOkClick()
          }}
          disabled={isConfirmLoading || primaryDisabledProp}
        >
          {finalPrimaryText}
        </Button>,
      )
    }

    if (buttons.length === 0) {
      return null
    }

    return <FooterContainer>{buttons}</FooterContainer>
  }

  const triggerNode = isValidElement(trigger) ? trigger : <button type='button'>{trigger}</button>

  return (
    <PopoverPrimitive.Root {...props} open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {triggerNode}
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          sideOffset={sideOffset}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            'z-max w-80 rounded-lg border border-main-border-default bg-main-background-card shadow-md outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className,
          )}
        >
          {Boolean(title || showClose) && (
            <div
              className={cn(
                'flex items-center pl-4 pr-1 pt-2',
                title ? 'justify-between' : 'justify-end',
              )}
            >
              {title && (
                <div className='text-main-foreground-primary text-heading-xxsmall'>{title}</div>
              )}
              {renderCloseButton()}
            </div>
          )}

          <div className='flex flex-col gap-1 p-2 text-sm'>{children}</div>

          {renderFooter()}

          {showArrow && <PopoverPrimitive.Arrow className='fill-white' />}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
